import { useState } from "react";
import { ChevronDown, RotateCcw } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { useTween } from "./AnimatedNumber";
import { computeRoi, defaultInputs, fmt, fmtPct, MAX_SUBMITTALS, num } from "@/lib/savings";

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="text-[15px] font-medium text-[#292929]">{children}</label>
);

const ScenarioRow = ({ s, i, onChange }) => (
  <div className="flex flex-col gap-3 border-t border-[#e2e2da] py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`calc-scenario-${i}`}>
    <div className="min-w-0">
      <p className="text-[15px] font-medium text-[#292929]">{s.title}</p>
      <p className="mt-0.5 text-[13px] leading-snug text-[#8a8a82]">{s.desc}</p>
    </div>
    <div className="flex h-11 shrink-0 items-center overflow-hidden rounded-xl border border-[#d8d8d0] bg-white font-mono text-sm">
      <span className="pl-3 text-[#8a8a82]">$</span>
      <input aria-label={`${s.title} cost`} inputMode="numeric" value={s.cost} onChange={onChange(i, "cost")} className="w-20 bg-transparent px-2 text-right outline-none" data-testid={`calc-scenario-${i}-cost`} />
      <span className="h-full w-px bg-[#d8d8d0]" />
      <input aria-label={`${s.title} share`} inputMode="numeric" value={s.share} onChange={onChange(i, "share")} className="w-12 bg-transparent px-2 text-right outline-none" data-testid={`calc-scenario-${i}-share`} />
      <span className="pr-3 text-[#8a8a82]">%</span>
    </div>
  </div>
);

const SmallField = ({ id, label, value, suffix, onChange }) => (
  <div className="flex items-center justify-between gap-4 border-t border-[#e2e2da] py-4">
    <label htmlFor={id} className="text-[15px] font-medium text-[#292929]">{label}</label>
    <div className="flex h-11 items-center rounded-xl border border-[#d8d8d0] bg-white font-mono text-sm">
      <input id={id} inputMode="numeric" value={value} onChange={onChange} className="w-16 bg-transparent px-3 text-right outline-none" data-testid={`${id}-input`} />
      <span className="pr-3 text-[#8a8a82]">{suffix}</span>
    </div>
  </div>
);

const Result = ({ id, label, display, final }) => (
  <div className="py-6 first:pt-0" data-testid={`calc-result-${id}`}>
    <p className="text-[15px] font-medium text-[#55554f]">{label}</p>
    <p className="mt-2 break-all font-display text-5xl font-semibold tracking-[-0.04em] text-[#ff6600] sm:text-6xl" data-testid={`calc-${id}-value`} data-value={final}>
      {display}
    </p>
  </div>
);

export const SavingsCalculator = () => {
  const [s, setS] = useState(defaultInputs);
  const [open, setOpen] = useState(false);
  const r = computeRoi(s);
  const saved = useTween(r.saved);
  const pct = useTween(r.timeSaved);
  const shares = Math.round(r.shareTotal * 100) / 100;

  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));
  const setSubmittals = (e) => {
    const v = e.target.value;
    setS((p) => ({ ...p, submittals: num(v) > MAX_SUBMITTALS ? String(MAX_SUBMITTALS) : v }));
  };
  const setScenario = (i, k) => (e) => {
    const v = e.target.value;
    setS((p) => ({ ...p, scenarios: p.scenarios.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  };

  return (
    <section id="savings-calculator" className="cd-section border-b border-[#bdbdb4]" data-testid="savings-calculator-section">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <SectionHeading id="savings-calculator" title="ROI calculator" />
        <div className="cd-reveal mt-12 grid items-stretch gap-5 lg:grid-cols-2">
          <div className="rounded-3xl border border-[#e2e2da] bg-[#fbfbf6] p-6 sm:p-10" data-testid="calc-inputs-card">
            <Label htmlFor="calc-submittals">Total submittals per year</Label>
            <input
              id="calc-submittals"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={s.submittals}
              onChange={setSubmittals}
              className="mt-3 h-16 w-full rounded-2xl border border-[#d8d8d0] bg-white px-5 font-display text-3xl font-semibold tracking-[-0.02em] text-[#292929] outline-none transition-[border-color,box-shadow] focus:border-[#ff6600] focus:shadow-[0_0_0_1px_#ff6600]"
              data-testid="calc-submittals-input"
            />
            <div className="mt-9 flex items-center justify-between">
              <Label htmlFor="calc-rate">Rejection rate</Label>
              <span className="font-display text-xl font-semibold text-[#292929]" data-testid="calc-rate-value">{s.rate}%</span>
            </div>
            <input id="calc-rate" type="range" min={0} max={100} step={1} value={s.rate} onChange={(e) => setS((p) => ({ ...p, rate: Math.round(Number(e.target.value)) }))} className="cd-range mt-5 w-full" data-testid="calc-rate-slider" />
            <div className="mt-9 border-t border-[#d8d8d0]" />
            <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between py-5 text-left" aria-expanded={open} data-testid="calc-advanced-toggle">
              <span className="text-[15px] font-medium text-[#292929]">Advanced</span>
              <ChevronDown className={`h-5 w-5 text-[#55554f] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            {shares !== 100 && (
              <p className="-mt-2 mb-3 font-mono text-[11px] text-[#c2410c]" data-testid="calc-shares-note">Shares total {shares}%</p>
            )}
            {open && (
              <div className="cd-expand" data-testid="calc-advanced-panel">
                {s.scenarios.map((x, i) => <ScenarioRow key={x.title} s={x} i={i} onChange={setScenario} />)}
                <SmallField id="calc-condoor-rate" label="Condoor rejection rate" value={s.condoorRate} suffix="%" onChange={set("condoorRate")} />
                <SmallField id="calc-manual-hours" label="Manual hours per submittal" value={s.manualHours} suffix="h" onChange={set("manualHours")} />
                <SmallField id="calc-condoor-hours" label="Condoor hours per submittal" value={s.condoorHours} suffix="h" onChange={set("condoorHours")} />
              </div>
            )}
          </div>
          <div className="flex flex-col rounded-3xl border border-[#e2e2da] bg-[#fbfbf6] p-6 sm:p-10" data-testid="calc-results-card">
            <div className="divide-y divide-[#e2e2da]">
              <Result id="saved" label="Saved per year" display={`$${fmt(saved)}`} final={`$${fmt(r.saved)}`} />
              <Result id="time-saved" label="% of time saved" display={fmtPct(pct)} final={fmtPct(r.timeSaved)} />
            </div>
            <button type="button" onClick={() => setS(defaultInputs())} className="mt-auto inline-flex items-center gap-1.5 self-end pt-6 text-sm text-[#55554f] underline-offset-4 transition-colors hover:text-[#d65300] hover:underline" data-testid="calc-reset-button">
              <RotateCcw className="h-3.5 w-3.5" /> Reset values
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
